/**
 * THE HUNT — canvas underneath, HUD on top.
 *
 * The canvas is driven by `Renderer` at frame rate and never re-renders React.
 * The HUD is React, ticked at 10 Hz — fast enough that the clock and the shell
 * counter feel live, slow enough that it costs nothing. Anything that has to be
 * frame-accurate (the crosshair, the reload arc, the floating score numbers) is
 * drawn on the canvas instead, which is why those live in `renderer.ts`.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { getSnapshot, reload as sendReload, subscribe } from '../net/connection.js';
import { clock } from '../net/clock.js';
import { seatColor } from './art.js';
import { audio } from './audio.js';
import type { AimController } from './aim.js';
import type { Range } from './range.js';
import { Renderer } from './renderer.js';
import { world } from './world.js';
import type { Settings } from '../ui/settings.js';
import { WAVES_PER_HUNT } from '@shared/sites.js';

interface Props {
  aim: AimController;
  settings: Settings;
  range: Range | null;
  onOpenSettings: () => void;
  onLeaveRange: (() => void) | null;
}

export function HuntScreen({ aim, settings, range, onOpenSettings, onLeaveRange }: Props) {
  const snap = useSyncExternalStore(subscribe, getSnapshot);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const [, tick] = useState(0);

  // ── the renderer ──────────────────────────────────────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const r = new Renderer(canvas, aim);
    rendererRef.current = r;
    r.start();

    const onResize = () => r.resize();
    window.addEventListener('resize', onResize);
    // iOS fires `orientationchange` before the viewport has settled, so the
    // resize is deferred a frame or the canvas comes back the old size.
    const onOrient = () => requestAnimationFrame(() => requestAnimationFrame(onResize));
    window.addEventListener('orientationchange', onOrient);
    screen.orientation?.addEventListener?.('change', onOrient);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onOrient);
      screen.orientation?.removeEventListener?.('change', onOrient);
      r.stop();
      rendererRef.current = null;
    };
  }, [aim]);

  // ── the HUD tick ──────────────────────────────────────────────────────────

  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 100);
    return () => clearInterval(id);
  }, []);

  // The range has no server to end it, so it is checked from here.
  useEffect(() => {
    if (!range || range.state.done) return;
    const id = setInterval(() => {
      if (range.check()) tick((n) => n + 1);
    }, 200);
    return () => clearInterval(id);
  }, [range]);

  // ── input ─────────────────────────────────────────────────────────────────

  /**
   * A press on a HUD control is not a shot.
   *
   * The whole screen is a trigger, and the HUD sits on top of it, so a press on
   * ⟳ or ☰ or FIRE also bubbles to this container. In gyro mode that meant
   * opening the settings sheet cost you a shell; on the FIRE button it fired
   * twice, once from the button and once from the bubble. Nobody had noticed
   * because the browser pass only ever asserted that FIRE fires *at all* — the
   * second shot was invisible to it. Buttons swallow the trigger now.
   */
  const onControl = (e: React.PointerEvent) => !!(e.target as Element).closest?.('button');

  const onDown = useCallback(
    (e: React.PointerEvent) => {
      // Only primary contact. A second thumb resting on the glass must not
      // become a second trigger.
      if (!e.isPrimary || onControl(e)) return;
      (e.target as Element).setPointerCapture?.(e.pointerId);
      aim.onPointerDown(e.nativeEvent);
    },
    [aim],
  );

  const onMove = useCallback(
    (e: React.PointerEvent) => {
      const fit = rendererRef.current?.stage();
      if (!fit) return;
      aim.onPointerMove(e.nativeEvent, fit);
    },
    [aim],
  );

  const onUp = useCallback(
    (e: React.PointerEvent) => {
      if (onControl(e)) return;
      aim.onPointerUp(e.nativeEvent);
    },
    [aim],
  );
  const onCancel = useCallback(() => aim.onPointerCancel(), [aim]);

  /**
   * KEYBOARD.
   *
   * Not a courtesy: on a desktop the mouse hand is aiming, which leaves the
   * other one with nothing to do, and reaching across to click a reload button
   * with the aiming hand costs the shot you were lining up. Space is the
   * trigger for anyone who would rather not click, R racks, C re-centres,
   * Esc opens the pause sheet. All of them are also on screen — WCAG 2.1.1
   * wants the keyboard to be sufficient, not required.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      // Never steal a key from a text field, and never from a focused button —
      // Space and Enter are that button's own activation.
      if (el && /^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(el.tagName)) return;

      switch (e.key) {
        case ' ':
        case 'Enter':
          e.preventDefault();
          aim.fire();
          return;
        case 'r':
        case 'R':
          e.preventDefault();
          if (!range) sendReload();
          return;
        case 'c':
        case 'C':
          e.preventDefault();
          aim.recenter();
          return;
        case 'Escape':
          e.preventDefault();
          onOpenSettings();
          return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [aim, range, onOpenSettings]);

  // ── derived HUD state ─────────────────────────────────────────────────────

  const t = clock.now();
  const view = snap.view;
  const isRange = !!range;
  const countdown = world.active ? world.countdown(t) : 0;
  const secondsLeft = world.active ? world.secondsLeft(t) : 0;
  const me = view?.seats.find((s) => s.id === snap.seatId);
  const spectating = !isRange && !!view && view.phase !== 'bar' && !me?.ready;

  const board = (view?.seats ?? [])
    .filter((s) => s.ready)
    .slice()
    .sort((a, b) => b.score - a.score);

  const canFire = world.active && world.shells > 0 && !world.reloadUntil && !world.benched && !spectating;

  /**
   * The countdown, out loud. Three woodblocks and a chime, which is the arcade
   * convention and also the only cue a player who is looking at the treeline
   * rather than at the number has that the wave is about to start.
   */
  const lastCount = useRef<number | null>(null);
  useEffect(() => {
    if (!world.active) {
      lastCount.current = null;
      return;
    }
    const prev = lastCount.current;
    lastCount.current = countdown;
    if (prev === null || countdown === prev) return;
    if (countdown > 0 && countdown <= 3) audio.play('tick');
    else if (countdown === 0 && prev > 0) audio.play('go');
  }, [countdown]);

  return (
    <div
      className={`hunt${settings.mode === 'mouse' ? ' pointer-aim' : ''}`}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onCancel}
    >
      <canvas ref={canvasRef} aria-label="The hunt" role="img" />

      <div className="hud">
        <div className="hud-top">
          <div className="hud-card">
            <div className="hud-wave">
              {isRange
                ? 'Practice range'
                : world.kind === 'varmint'
                  ? `Wave ${world.waveIndex} · varmints`
                  : world.kind === 'legend'
                    ? `Wave ${world.waveIndex} · the legend`
                    : `Wave ${world.waveIndex} of ${WAVES_PER_HUNT}`}
            </div>
            <div className="hud-site">{world.site.name}</div>
          </div>

          {world.active && countdown === 0 && <div className="hud-clock hud-card">{secondsLeft}</div>}

          <div className="row" style={undefined}>
            <button className="round-btn" onClick={() => aim.recenter()} aria-label="Re-centre the sight" title="Re-centre">
              ⊕
            </button>
            <button className="round-btn" onClick={onOpenSettings} aria-label="Settings">
              ☰
            </button>
          </div>
        </div>

        {!isRange && board.length > 0 && (
          <div className="board" aria-label="Scores">
            {board.map((s) => (
              <div
                key={s.id}
                className={`board-row${s.id === snap.seatId ? ' is-me' : ''}${s.benched ? ' is-benched' : ''}`}
              >
                <Dot hue={s.hue} />
                <span className="name">{s.handle}</span>
                <span className="tabular">{s.score.toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}

        <div className="feed" aria-live="polite">
          {world.feed.map((f, i) => (
            <div key={`${f.born}-${i}`} className={`feed-item ${f.tone}`}>
              {f.text}
            </div>
          ))}
        </div>

        <div className="hud-bottom">
          <div className="shells" aria-label={`${world.shells} of ${world.maxShells} shells`}>
            {Array.from({ length: world.maxShells }, (_, i) => (
              <span key={i} className={`shell${i < world.shells ? '' : ' spent'}`} />
            ))}
            <button
              className="round-btn"
              onClick={() => (isRange ? undefined : sendReload())}
              disabled={world.shells >= world.maxShells || !!world.reloadUntil}
              aria-label="Reload"
              title="Reload"
            >
              ⟳
            </button>
          </div>

          {/* Desktop only, and `aria-hidden` because every one of these is a
              real on-screen control a screen-reader user already has. */}
          {settings.mode === 'mouse' && (
            <div className="keys" aria-hidden="true">
              <kbd>Click</kbd> fire<span>·</span>
              <kbd>R</kbd> reload<span>·</span>
              <kbd>Esc</kbd> menu
            </div>
          )}

          {settings.fireButton && !spectating && (
            <button className="fire-btn" onClick={() => aim.fire()} disabled={!canFire} aria-label="Fire">
              FIRE
            </button>
          )}
        </div>
      </div>

      <Overlays
        isRange={isRange}
        range={range}
        countdown={countdown}
        spectating={spectating}
        onLeaveRange={onLeaveRange}
        snap={snap}
      />
    </div>
  );
}

function Dot({ hue }: { hue: number }) {
  return (
    <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true">
      <circle cx="5" cy="5" r="4.5" fill={seatColor(hue)} />
    </svg>
  );
}

// ── the cards that sit over the scene ───────────────────────────────────────

function Overlays({
  isRange,
  range,
  countdown,
  spectating,
  onLeaveRange,
  snap,
}: {
  isRange: boolean;
  range: Range | null;
  countdown: number;
  spectating: boolean;
  onLeaveRange: (() => void) | null;
  snap: ReturnType<typeof getSnapshot>;
}) {
  if (snap.restarting) {
    return (
      <div className="slate">
        <p className="mid">The saloon is closing for a moment</p>
        <p className="muted">Back in a few seconds. Your seat is held.</p>
      </div>
    );
  }

  if (isRange && range?.state.done) {
    const good = range.state.hits > 0;
    return (
      <div className="slate">
        <p className="big">{good ? 'That will do' : 'Have another go'}</p>
        <p className="mid">
          {range.state.hits} of {range.state.shots} on target
        </p>
        <p className="muted">
          {range.state.doeShot
            ? 'You shot the doe. In a real wave that benches you and everyone hears about it — antlers or nothing.'
            : 'You left the doe alone. That is the whole game.'}
        </p>
        <div className="row mt-md">
          <button className="btn" onClick={() => range.start()}>
            Again
          </button>
          <button className="btn btn-primary" onClick={onLeaveRange ?? undefined}>
            Back to the bar
          </button>
        </div>
      </div>
    );
  }

  if (countdown > 0) {
    return (
      <div className="slate">
        <p className="big">{countdown}</p>
        <p className="mid">{world.site.name}</p>
        <p className="muted">{world.site.blurb}</p>
        {world.kind === 'varmint' && <p className="mid">Varmints — clear them out, leave the skunk</p>}
        {world.kind === 'legend' && <p className="mid">Something big is out there. Four shells.</p>}
      </div>
    );
  }

  if (snap.summary) {
    const s = snap.summary;
    return (
      <div className="slate">
        <p className="mid">
          Wave {s.waveIndex} · {s.siteName}
        </p>
        <div className="list">
          {s.rows.map((r) => (
            <div key={r.seatId} className="standing">
              <Dot hue={r.hue} />
              <div className="stack grow">
                <div className="who">{r.handle}</div>
                <div className="stat">
                  {r.kills} taken{r.does > 0 ? ` · ${r.does} doe` : ''}
                </div>
              </div>
              <span className="score">
                {r.gained >= 0 ? '+' : ''}
                {r.gained.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
        <p className="muted mt-md">
          {snap.summaryNextAt ? 'Next site coming up…' : 'That is the hunt.'}
        </p>
      </div>
    );
  }

  if (spectating) {
    return (
      <div className="slate">
        <p className="mid">Watching</p>
        <p className="muted">You walked in mid-hunt. Ante up and you are in on the next round.</p>
      </div>
    );
  }

  return null;
}
